import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Spinner, Tabs, Tab, Col, Row, TabContainer } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { utils } from "@tradetrust-tt/tradetrust";
import '../css/custom.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import moment from 'moment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DocumentViewer from './DocumentViewer';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PDFDocument } from 'pdf-lib';

function Viewer() {
    const location = useLocation();
    const navigate = useNavigate();
    const documentJson = location?.state?.document || "";
    let rId = location?.state?.rId || "";
    const country = location?.state?.country || "";
    const verificationType = location?.state?.verificationType || "";
    const [originalDocument, setOriginalDocument] = useState("");
    const [issuerUrl, setIssuerUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isENotary, setIsENotary] = useState(false);
    const [tabKey, setTabKey] = useState("0");
    const [hasDocumentIntegrityError, setHasDocumentIntegrityError] = useState(false);
    const [hasDocumentStatusError, setHasDocumentStatusError] = useState(false);
    const [hasIssuerIdentityError, setHasIssuerIdentityError] = useState(false);
    const [hasAcraDoc, setHasAcraDoc] = useState(false);
    const [acraDoc, setAcraDoc] = useState([]);
    const [finishedVerification, setFinishedVerification] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [oaFileName, setOAFileName] = useState("");
    const [oaPDFFileName, setOAPDFFileName] = useState("");
    const isOADoc = utils.isSignedWrappedV2Document(documentJson) || utils.isSignedWrappedV3Document(documentJson);
    const isProtected = JSON.parse(process.env.REACT_APP_IS_PROTECTED);
    const [dataFromIframe, setDataFromIframe] = useState([]);
    const [dataFromIframeSorted, setDataFromIframeSorted] = useState([]);
    const viewerRef = useRef();

    function onVerifyClick() {
        navigate('/');
    }

    useEffect(() => {
        console.log(`isOADoc ${isOADoc}`);
        if (isOADoc) {
            const authenticateOptions = {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }
            setIsVerifying(true);
            fetch(`${process.env.REACT_APP_WHITELIST_URL}`, authenticateOptions)
                .then(response => response.json())
                .then(json => checkWhitelistUrl(json))
                .then(() => {
                    const authenticateOptions = {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json', 'apikey': process.env.REACT_APP_LEGAL_TRUST_APIKEY },
                    }
    
                    return fetch(`${process.env.REACT_APP_VERIFICATION_ENDPOINT}/authenticate`, authenticateOptions);
                })
                .then(response => response.text())
                .then(data => {
                    const requestOptions = {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-jwt-token': data },
                        body: JSON.stringify(documentJson)
                    };
    
                    return fetch(`${process.env.REACT_APP_VERIFICATION_ENDPOINT}/verification/verify/did`, requestOptions);
                })
                .then(response => response.json())
                .then(json => fetchResponseHandler(json, isOADoc))
                .catch(error => {
                    console.log(error);
                    setHasDocumentIntegrityError(true);
                    setHasDocumentStatusError(true);
                    setHasIssuerIdentityError(true);
                    setFinishedVerification(true);
                    setIsVerifying(false);
                    setErrorMessage(error);
                })
        } else {
            const authenticateOptions = {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }
            setIsVerifying(true);
            fetch(`${process.env.REACT_APP_WHITELIST_URL}`, authenticateOptions)
                .then(response => response.json())
                .then(json => checkWhitelistUrl(json))
                .then(() => {
                    const authenticateOptions = {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json', 'apikey': process.env.REACT_APP_LEGAL_TRUST_APIKEY },
                    }
                    return fetch(`${process.env.REACT_APP_VERIFICATION_ENDPOINT}/authenticate`, authenticateOptions)
                })
                .then(response => response.text())
                .then(data => {
                    const requestOptions = {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-jwt-token': data },
                        body: JSON.stringify(documentJson)
                    };

                    return fetch(`${process.env.REACT_APP_VERIFICATION_ENDPOINT}/verification/verify/w3c`, requestOptions);
                })
                .then(response => response.json())
                .then(json => fetchResponseHandler(json, isOADoc))
                .catch(error => {
                    setHasDocumentIntegrityError(true);
                    setHasDocumentStatusError(true);
                    setHasIssuerIdentityError(true);
                    setFinishedVerification(true);
                    setIsVerifying(false);
                    setErrorMessage(error);
                });
        }
    }, 
    // eslint-disable-next-line
    [])

    function fetchResponseHandler(json, isOADoc) {
        setIsVerifying(false);
		if (json.error) {
			setErrorMessage(json.error);
			setHasDocumentIntegrityError(true);
			setHasDocumentStatusError(true);
			setHasIssuerIdentityError(true);
			setFinishedVerification(true);
			return;
		}
        for (const eachVerification of json) {
            if (eachVerification.status === "VALID") {
                continue;
            }
            else if (eachVerification.status === "SKIPPED") {
                continue;
            }
            else {
                if (eachVerification.type === "DOCUMENT_INTEGRITY") {
                    setErrorMessage(new Error("Document Identity is invalid."));
                    setHasDocumentIntegrityError(true);
                } else if (eachVerification.type === "DOCUMENT_STATUS") {
                    setErrorMessage(new Error("Document Status is invalid."));
                    setHasDocumentStatusError(true);
                } else {
                    setErrorMessage(new Error("Issuer Identity is invalid."));
                    setHasIssuerIdentityError(true);
                }
            }
        }

        let extractedDocument;
        if (isOADoc) {
            setIssuerUrl(json.filter(verification => verification.type === "ISSUER_IDENTITY" && verification.status === "VALID")[0].data[0].location);

            extractedDocument = utils.getDocumentData(documentJson);
            setOriginalDocument(extractedDocument);
            if (extractedDocument.certifiedFor) {
                let tempAcraDoc = [];
                for (const singleDoc of extractedDocument.certifiedFor) {
                    tempAcraDoc = [...tempAcraDoc, singleDoc];
                }
                setHasAcraDoc(true);
                setAcraDoc(tempAcraDoc);
            }
        } else {
            const match = documentJson.issuer.match(/[^:]+$/);
            const issuerUrl = match ? match[0] : null;
            setIssuerUrl(issuerUrl);
            /* old viewer code
            // extractedDocument = documentJson.credentialSubject;
			// if (documentJson["$template"] && !extractedDocument["$template"]) {
			// 	extractedDocument = {...extractedDocument, "$template": documentJson["$template"]}
            // }
            // setOriginalDocument(extractedDocument);
            // if (extractedDocument.certifiedFor) {
            //     const decodedAcraData = JSON.parse(atob(extractedDocument.certifiedFor));
            //     let tempAcraDoc = [];
            //     for (const singleDoc of decodedAcraData) {
            //         const originalSingleDoc = utils.getDocumentData(singleDoc);
            //         tempAcraDoc = [...tempAcraDoc, originalSingleDoc];
            //     }
            //     setHasAcraDoc(true);
            //     setAcraDoc(tempAcraDoc);
            // }
            */

            console.log(documentJson);
            setOriginalDocument(documentJson);
            if (documentJson.credentialSubject.certifiedFor) {
                const decodedAcraData = JSON.parse(Buffer.from(documentJson.credentialSubject.certifiedFor, 'base64'));
                let tempAcraDoc = [];
                if (Array.isArray(decodedAcraData)) {
                    for (const singleDoc of decodedAcraData) {
                        tempAcraDoc = [...tempAcraDoc, singleDoc];
                    }
                } else {
                    tempAcraDoc = [...tempAcraDoc, decodedAcraData];
                }
                
                setHasAcraDoc(true);
                setAcraDoc(tempAcraDoc);
            }
            if (documentJson.renderMethod.templateName) {
                if (documentJson.renderMethod.templateName === "eNotaryTemplate") {
                    setIsENotary(true);
                }
            }
        }
		setFinishedVerification(true);
    }

    function checkWhitelistUrl(json) {
        try {
            let extractedDocument, tempIssuerUrl;
            if (isOADoc) {
                extractedDocument = utils.getDocumentData(documentJson);
                if (extractedDocument && extractedDocument.issuers) {
                    tempIssuerUrl = extractedDocument.issuers[0].identityProof.location;
                }
            } else {
                const match = documentJson.issuer.match(/[^:]+$/);
                tempIssuerUrl = match ? match[0] : null;
            }
            const existingWhitelistedUrl = json.filter(appConfig => appConfig.value.includes(tempIssuerUrl));
            if (!existingWhitelistedUrl || existingWhitelistedUrl.length === 0) {
                throw new Error("Issuer URL is not whitelisted. Please contact administrator.");
            }
        } catch (error) {
            throw error;
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (!finishedVerification)
                return;
            const isValid = errorMessage === null;
    
            if (!rId) {
                const merkleRoot = getMerkleRootFromDocumentJson(documentJson);
                if (merkleRoot) {
                    const authenticateOptions = {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json', 'apikey': process.env.REACT_APP_LEGAL_TRUST_APIKEY },
                    }
                    const jwtResponse = await fetch(`${process.env.REACT_APP_VERIFICATION_ENDPOINT}/authenticate`, authenticateOptions);
                    if (!jwtResponse.ok) throw new Error("Authentication failed");
                    const jwt = await jwtResponse.text();

                    const recordRequestOptions = {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json', 'x-jwt-token': jwt }
                    };
                    const recordResponse = await fetch(`${process.env.REACT_APP_VERIFICATION_ENDPOINT}/record/root/${merkleRoot}`, recordRequestOptions);
                    if (!recordResponse.ok) throw new Error("Record request failed"); 
                    rId = await recordResponse.text();
                }
            }

            const postBody = {
                verificationType: verificationType,
                country: country,
                rId: rId,
                isValid: isValid,
                oaFileName: oaFileName,
                invalidMessage: errorMessage
            }
            const urlEncodedData = new URLSearchParams(postBody).toString();
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: urlEncodedData
            };
    
            fetch(`${process.env.REACT_APP_VERIFICATION_REPORT}`, requestOptions)
            .then(response => response.text())
            .then(text => console.log(text))
            .catch(error => {
                console.error(error);
            });
        }
        
        fetchData();
    }, [finishedVerification]);

    function onDownloadButtonClick() {
        const downloadButton = document.createElement('a');
        const formJsonConverted = JSON.stringify(documentJson);
        const file = new Blob([formJsonConverted], {type: 'application/json'});
        downloadButton.href = URL.createObjectURL(file);
        downloadButton.download = oaFileName;
        document.body.appendChild(downloadButton);
        downloadButton.click(); 
        document.body.removeChild(downloadButton);
    }

    async function onDownloadAsPDFButtonClick() {
        try {
            // #region Download pdf via url from SAL
            const downloadUrl = documentJson.credentialSubject.pdfUrl;
            if (downloadUrl) {
                const response = await fetch(downloadUrl, {
                    mode: 'cors',
                });
                if (response.status === 200) {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    const downloadButton = document.createElement('a');
                    downloadButton.href = url;
                    downloadButton.download = oaPDFFileName;
                    document.body.appendChild(downloadButton);
                    downloadButton.click(); 
                    document.body.removeChild(downloadButton);
                } else {
                    alert("Failed to retrieve PDF file.");
                }
            } else {
                alert("The PDF version of this e-Apostille (.json/.vc2) is not available. Please contact administrator.");
            }
            // #endregion

            // #region Generate PDF via postMessage from renderer
            // const inputs = dataFromIframeSorted;
            // console.log(inputs);
            // //const pdfGenerator = new jsPDF('p', 'pt', 'a4');
            // const finalPDFDoc = await PDFDocument.create();

            // if (inputs && inputs.length > 0) {
            //     for (let i = 0; i < inputs.length; i++) {
            //         if (inputs[i].type.includes('PDF')) {
            //             await GeneratePDFPageFromBase64String(inputs[i].data, finalPDFDoc);
            //         } else {
            //             await GeneratePDFPageFromHTMLString(inputs[i].data, finalPDFDoc);
            //         }
            //     }
            //     //pdfGenerator.save(oaPDFFileName);

            //     const pdfBytes = await finalPDFDoc.save();
            //     const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            //     const link = document.createElement('a');
            //     link.href = URL.createObjectURL(blob);
            //     link.download = oaPDFFileName;
            //     document.body.appendChild(link);
            //     link.click();
            //     document.body.removeChild(link);
            // } else {
            //     alert("Unable to retrieve PDF generation data.");
            // }
            // #endregion

            // #region using TT Framework ref print
            // if (viewerRef.current) {
            //     viewerRef.current.print();
            // }
            // #endregion

        } catch (error) {
            console.error(error);
        }
    }

    window.addEventListener('message', (event) => {
        if (event.data.type && event.data.type.includes('IFRAME_DOC_DIV')) {
            const receivedHTML = event.data.data;
            setDataFromIframe(prevData => {
                const index = prevData.findIndex(item => item.type === event.data.type && item.data === event.data.data);
                const newItem = { type: event.data.type, data: receivedHTML };

                if (index !== -1) {
                    const updated = [...prevData];
                    updated[index] = newItem;
                    return updated;
                } else {
                    return [...prevData, newItem];
                }
            });
        }
    });

    useEffect(() => {
        const sorted = [...dataFromIframe].sort((a, b) => {
            const order = {
                'IFRAME_DOC_DIV_EAPOSTILLE': 1,
                'IFRAME_DOC_DIV_ENOTARY': 2,
                'IFRAME_DOC_DIV_PDF': 3,
            };

            return (order[a.type] || 99) - (order[b.type] || 99);
        });

        setDataFromIframeSorted(sorted);
    }, [dataFromIframe]);

    async function GeneratePDFPageFromHTMLString(htmlString, finalPDFDoc) {
        // return new Promise((resolve) => {
        //     const tempContainer = document.createElement('iframe');
        //     tempContainer.srcdoc = htmlString;
        //     tempContainer.style.position = 'fixed';
        //     tempContainer.style.left = '-9999px';
        //     document.body.appendChild(tempContainer);

        //     tempContainer.onload = () => {
        //         const iframeDoc = tempContainer.contentDocument || tempContainer.contentWindow.document;
        //         const iframeBody = iframeDoc.body;
        //         html2canvas(iframeBody, {
        //             scale: 2,
        //             width: 707,
        //             height: 1000,
        //             windowWidth: 707,
        //             windowHeight: 1000
        //         }).then((canvas) => {
        //             const imgData = canvas.toDataURL('image/png');
                    
        //             const imgProps = pdfGenerator.getImageProperties(imgData);
        //             const pdfWidth = pdfGenerator.internal.pageSize.getWidth();
        //             const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        //             if (index > 0) {
        //                 pdfGenerator.addPage();
        //             }
        //             pdfGenerator.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        //             document.body.removeChild(tempContainer);
        //             resolve();
        //         });
        //     }
        // })

        return new Promise((resolve) => {
            const tempContainer = document.createElement('iframe');
            tempContainer.srcdoc = htmlString;
            tempContainer.style.position = 'fixed';
            tempContainer.style.left = '-9999px';
            document.body.appendChild(tempContainer);

            tempContainer.onload = async () => {
                const iframeDoc = tempContainer.contentDocument || tempContainer.contentWindow.document;
                const iframeBody = iframeDoc.body;
                const canvas = await html2canvas(iframeBody, {
                    scale: 2,
                    width: 707,
                    height: 1000,
                    windowWidth: 707,
                    windowHeight: 1000
                });
                const imgData = canvas.toDataURL('image/png');
                const imgBytes = await fetch(imgData).then(res => res.arrayBuffer());
                const img = await finalPDFDoc.embedPng(imgBytes);
                console.log(canvas.width, canvas.height);
                const page = finalPDFDoc.addPage([canvas.width, canvas.height]);
                page.drawImage(img);
                resolve();
            }
        })
        
    }

    async function GeneratePDFPageFromBase64String(base64String, finalPDFDoc) {
        const pdfBytes = Uint8Array.from(atob(base64String.split(',')[1]), c => c.charCodeAt(0));
        const pdfDoc = await PDFDocument.load(pdfBytes);

        const copiedPages = await finalPDFDoc.embedPdf(pdfDoc, pdfDoc.getPageIndices());

        const targetWidth = 1414;
        const targetHeight = 2000;

        copiedPages.forEach(page => {
            //finalPDFDoc.addPage(page);

            const newPage = finalPDFDoc.addPage([targetWidth, targetHeight]);
            const originalWidth = page.width;
            const originalHeight = page.height;

            const scaleX = targetWidth / originalWidth;
            const scaleY = targetHeight / originalHeight;
            const scale = Math.min(scaleX, scaleY);
            const scaledWidth = originalWidth * scale;
            const scaledHeight = originalHeight * scale;

            const xOffset = (targetWidth - scaledWidth) / 2;
            const yOffset = (targetHeight - scaledHeight) / 2;
            newPage.drawPage(page, {
                x: xOffset,
                y: yOffset,
                xScale: scale,
                yScale: scale
            });
        });
    }

    useEffect(() => {
        const eapostilleNo = documentJson?.credentialSubject?.apostilleNo ?? "";
        setOAFileName(`SAL_eApostille_${eapostilleNo}.json`);
    },[])

    useEffect(() => {
        const eapostilleNo = documentJson?.credentialSubject?.apostilleNo ?? "";
        setOAPDFFileName(`SAL_eApostille_${eapostilleNo}.pdf`);
    },[])

    function getMerkleRootFromDocumentJson(documentJson) {
        if (!documentJson || !documentJson.signature || !documentJson.signature.merkleRoot) 
            return "";
        else 
            return documentJson.signature.merkleRoot;
    }

    if (errorMessage) {
        console.error(errorMessage);
    }

    return (
        <div className='document-container-main'>
            <div className="doc-process">
                <ul>
                    <li>
                        {isVerifying ? <div className="doc-loader">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div> : <div> {!hasDocumentStatusError ? 
                                    <div className="doc-success"><FontAwesomeIcon icon={faCircleCheck} /></div>
                                    : 
                                    <div className="doc-error"><FontAwesomeIcon icon={faCircleXmark} /></div>}</div>}
                        <span className="loading hidden">This document is valid and has not been revoked</span>
                    </li>
                    <li>
                        {isVerifying ? <div className="doc-loader">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div> : <div> {!hasDocumentIntegrityError ? 
                                    <div className="doc-success"><FontAwesomeIcon icon={faCircleCheck} /></div>
                                    : 
                                    <div className="doc-error"><FontAwesomeIcon icon={faCircleXmark} /></div>}</div>}
                        <span className="loading hidden">This document has not been tampered with</span>
                    </li>
                    <li>
                        {isVerifying ? <div className="doc-loader">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div> : <div> {!hasIssuerIdentityError ? 
                                    <div className="doc-success"><FontAwesomeIcon icon={faCircleCheck} /></div>
                                    : 
                                    <div className="doc-error"><FontAwesomeIcon icon={faCircleXmark} /></div>}</div>}
                        <span className="loading hidden">This document was issued by the Singapore Academy of Law</span>
                    </li>
                </ul>
            </div>
            <div className='dottet-panel'>
                <div>
                    <div>
                        {!hasDocumentIntegrityError && !hasDocumentStatusError && !hasIssuerIdentityError ?
                        <div>
                            <div style={{marginBottom: "20px"}}>
                            <Row>
                                <Col style={{display:'flex', justifyContent:'left', flexDirection:'column'}} sm={11}>
                                    {!isVerifying && <h3 style={{textAlign: "left", color: "darkblue"}}>Certified by the Singapore Academy of Law</h3>}
                                    {!isVerifying && documentJson && documentJson.credentialSubject && documentJson.credentialSubject.certifiedOn && <h3 style={{textAlign: "left", color: "darkblue"}}>Certificate Created: {moment(documentJson.credentialSubject.certifiedOn).format("DD MMM YYYY & HH:mm:ss")}</h3>}
                                </Col>
                                {/* <Col style={{display:'flex', justifyContent:'right'}} sm={1}>
                                    <Button variant='outline-primary' onClick={onDownloadButtonClick}><FileDownloadIcon></FileDownloadIcon></Button>
                                </Col> */}
                            </Row>
                            
                            </div>
                            {/* <div>
                                <Tabs onSelect={id => setSelectedTemplate(id)}>
                                    {templateList.map(template => (
                                        <Tab eventKey={template.id} title={template.label}/>
                                    ))}
                                </Tabs>
                            </div> */}
                            <Row>
                                <Col sm={11}>
                                { !isENotary ? (
                                    <Tabs activeKey={tabKey} onSelect={id => setTabKey(id)}>
                                    <Tab eventKey="0" title="e-Apostille">
                                        {isLoading && <div style={{display: 'flex', justifyContent: 'center', marginTop: 25}}><Spinner animation="border" role="status"></Spinner></div>}
                                        {originalDocument && <DocumentViewer renderDoc={originalDocument} ref={viewerRef}/>}
                                    </Tab>
                                    {hasAcraDoc && acraDoc.map((singleAcraDoc, key) => (
                                        <Tab eventKey={key+1} title={singleAcraDoc?.credentialSubject?.pdfName ?? "Public Document"} key={key}>
                                            <DocumentViewer renderDoc={singleAcraDoc} ref={viewerRef} />
                                        </Tab>
                                    ))}
                                    </Tabs>
                                ) : (
                                    <Tabs activeKey={tabKey} onSelect={id => setTabKey(id)}>
                                        {hasAcraDoc && acraDoc[0] && acraDoc[0].credentialSubject?.pdfName === "e-NC" && 
                                            <Tab eventKey={0} 
                                                title={acraDoc[0].credentialSubject?.pdfName} 
                                                key={0}>
                                                {isLoading && <div style={{display: 'flex', justifyContent: 'center', marginTop: 25}}><Spinner animation="border" role="status"></Spinner></div>}
                                                <DocumentViewer renderDoc={acraDoc[0]} ref={viewerRef} />
                                            </Tab>
                                        }
                                        <Tab eventKey="1" title="e-Apostille">
                                            {originalDocument && <DocumentViewer renderDoc={originalDocument} ref={viewerRef}/>}
                                        </Tab>
                                        {hasAcraDoc && acraDoc.map((singleAcraDoc, key) => {
                                            if (key === 0) {
                                                return <div style={{display: 'none'}}></div>
                                            } else {
                                                return <Tab eventKey={singleAcraDoc?.credentialSubject?.pdfName === "e-NC" ? "0" : key+1} 
                                                    title={singleAcraDoc?.credentialSubject?.pdfName ?? "Public Document"} 
                                                    key={singleAcraDoc?.credentialSubject?.pdfName === "e-NC" ? "0" : key}>
                                                    <DocumentViewer renderDoc={singleAcraDoc} ref={viewerRef} />
                                                </Tab> 
                                            }
                                        })}
                                    </Tabs>
                                )}
                                </Col>
                                {<Col sm={1}>
                                    {!isProtected && !isVerifying && <Button style={{marginTop: "20px"}} variant='outline-primary' onClick={onDownloadButtonClick} title='Download .json file'><FileDownloadIcon/></Button>}
                                    {!isVerifying && <Button style={{marginTop: "20px"}} variant='outline-primary' onClick={onDownloadAsPDFButtonClick} title='Download PDF file'><PictureAsPdfIcon/></Button>}
                                </Col>}
                            </Row>
                        </div> :
                        <div>
                            <div className="dropzone-icon dropzone-failed hidden"></div>
                            <h4 className="error hidden">The uploaded certificate verification has failed<small>Possible reasons include the certificate being tampered with, revoked, or other issues.</small></h4>
                            {/* <hr/>
                            {errorMessage ? (errorMessage.message ? errorMessage.message : errorMessage) : <p className="text-center hidden">Please contact the help desk for assistance.</p>} */}
                        </div>}
                    </div>
                    <hr/>
                    {!isProtected && <Button variant="outline-primary" onClick={onVerifyClick} style={{ textTransform: "none" }}>Try another e-Apostille (.json)</Button>}
                </div>
            </div>
        </div>
    );
};
  
export default Viewer;